import React, { Component, Fragment } from 'react';
import { Button, ButtonGroup, Card, FormLayout, Stack, Heading, Checkbox } from '@envoy/polarwind';
import ChoiceColumn from './ChoiceColumn';
import ChoiceColumnAction from './ChoiceColumnAction';
import RuleFieldMapping from './RuleFieldMapping';
import RuleReferenceMapping from './RuleReferenceMapping';
import Select from './components/Select';
import './Rule.css';

class Rule extends Component {

  state = { tableColumnOptions: null, error: null };

  async componentDidMount() {
    const { rule: { table }, api } = this.props;
    if (table) {
      this.setState({
        tableColumnOptions: await api.getTableColumnOptions(table),
      });
    }
  }

  onChangeEvent = event => {
    this.props.onChange({ ...this.props.rule, event }, this.props.index);
  };

  onChangeVisitorType = visitorType => {
    this.props.onChange({ ...this.props.rule, visitorType }, this.props.index);
  };

  onChangeAction = action => {
    try {
      this.props.onChange({ ...this.props.rule, action }, this.props.index);
      if (this.state.error) {
        this.setState({ error: null });
      }
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  onChangeTable = table => {
    try {
      this.props.onChange(
        { ...this.props.rule, table, fieldMappings: [], referenceMappings: [] },
        this.props.index,
      );
      this.setState({ tableColumnOptions: null, error: null }, async () => this.setState({
        tableColumnOptions: await this.props.api.getTableColumnOptions(table),
      }));
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  onAddFieldMapping = () => {
    const { rule: { event, action } } = this.props;
    const { tableColumnOptions } = this.state;
    const mapping = {
      envoyField: Rule.envoyFieldOptions[event][0].value,
      serviceNowColumn: tableColumnOptions.find(t => !t.reference).value,
      isLookupColumn: action === 'find',
      choiceMappings: [],
    }
    this.props.onChange({
      ...this.props.rule,
      fieldMappings: this.props.rule.fieldMappings.concat(mapping),
      },
      this.props.index,
    );
  };

  onRemoveFieldMapping = index => {
    this.props.onChange({
        ...this.props.rule,
        fieldMappings: this.props.rule.fieldMappings.filter((c, i) => i !== index),
      },
      this.props.index,
    );
  };

  onChangeFieldMapping = (mapping, index) => {
    this.props.onChange({
        ...this.props.rule,
        fieldMappings: this.props.rule.fieldMappings.map((m, i) => i === index ? mapping : m),
      },
      this.props.index,
    );
  };

  onChangeAddAttachment = addAttachment => {
    this.props.onChange({ ...this.props.rule, addAttachment }, this.props.index);
  };

  onChangeChoiceColumnAction = (action, serviceNowColumn) => {
    const { rule: { fieldMappings } } = this.props;
    const index = fieldMappings.findIndex(m => m.serviceNowColumn === serviceNowColumn);
    this.onChangeFieldMapping({ ...fieldMappings[index], action }, index);
  };

  onAddReferenceMapping = () => {
    const { rules: [rule] } = this.props;
    const { tableColumnOptions } = this.state;
    const envoyRule = `${rule.action}|${rule.table}`
    const mapping = {
      envoyRule,
      serviceNowColumn: tableColumnOptions.find(t => t.reference).value,
      isRequired: false,
    }
    this.props.onChange({
        ...this.props.rule,
        referenceMappings: this.props.rule.referenceMappings.concat(mapping),
      },
      this.props.index,
    );
  };

  onRemoveReferenceMapping = index => {
    this.props.onChange({
        ...this.props.rule,
        referenceMappings: this.props.rule.referenceMappings.filter((c, i) => i !== index),
      },
      this.props.index,
    );
  };

  onChangeReferenceMapping = (mapping, index) => {
    this.props.onChange({
        ...this.props.rule,
        referenceMappings: this.props.rule.referenceMappings.map((m, i) => i === index ? mapping : m),
      },
      this.props.index,
    );
  };

  onEdit = () => {
    this.props.onEdit(this.props.index);
  };

  get visitorTypes() {
    const { visitorTypeOptions } = this.props;
    const { event, visitorType } = this.props.rule;
    if (event !== Rule.EVENT_SIGN_IN) {
      return null;
    }
    return (
      <Select
        label="Visitor Type"
        isLoading={!visitorTypeOptions}
        options={visitorTypeOptions}
        value={visitorType}
        onChange={this.onChangeVisitorType}
        required
      />
    );
  }

  get fieldMappings() {
    const { fieldMappings, event, action } = this.props.rule;
    const { tableColumnOptions } = this.state;
    if (!tableColumnOptions) {
      return null;
    }
    const serviceNowColumnOptions = tableColumnOptions.filter(f => !f.reference);
    return fieldMappings.map((mapping, index) => (
      <RuleFieldMapping
        key={index}
        index={index}
        mapping={mapping}
        envoyFieldOptions={Rule.envoyFieldOptions[event]}
        serviceNowColumnOptions={serviceNowColumnOptions}
        action={action}
        onRemove={this.onRemoveFieldMapping}
        onChange={this.onChangeFieldMapping}
      />
    ));
  }

  get referenceMappings() {
    const { tableOptions, rules, rule: { referenceMappings, action } } = this.props;
    const { tableColumnOptions } = this.state;
    if (!tableColumnOptions) {
      return null;
    }
    const envoyRuleOptions = rules.map(rule => {
      const table = tableOptions.find(option => option.value === rule.table);
      const action = Rule.actionOptions.find(option => option.value === rule.action);
      return {
        label: `${action.label}: ${table.label}`,
        value: `${rule.action}|${rule.table}`,
      }
    });
    const serviceNowColumnOptions = tableColumnOptions.filter(f => f.reference);
    return referenceMappings.map((mapping, index) => (
      <RuleReferenceMapping
        key={index}
        index={index}
        mapping={mapping}
        envoyRuleOptions={envoyRuleOptions}
        serviceNowColumnOptions={serviceNowColumnOptions}
        action={action}
        onRemove={this.onRemoveReferenceMapping}
        onChange={this.onChangeReferenceMapping}
      />
    ));
  }

  get choiceColumnsWithFieldMappings() {
    const { tableColumnOptions } = this.state;
    const { rule: { fieldMappings } } = this.props;
    if (!tableColumnOptions) {
      return [];
    }
    return tableColumnOptions.reduce((choiceColumnsWithMappings, column) => {
      if (!column.isChoice) {
        return choiceColumnsWithMappings;
      }
      const mappingIndex = fieldMappings.findIndex(mapping => mapping.serviceNowColumn === column.value);
      if (mappingIndex === -1) {
        return choiceColumnsWithMappings;
      }
      const mapping = fieldMappings[mappingIndex];
      return choiceColumnsWithMappings.concat({ column, mapping, mappingIndex });
    }, []);
  }

  get choiceColumnActions() {
    return this.choiceColumnsWithFieldMappings.map(({ column, mapping, mappingIndex }) => (
      <ChoiceColumnAction
        key={mappingIndex}
        column={column}
        mapping={mapping}
        mappingIndex={mappingIndex}
        onChangeFieldMapping={this.onChangeFieldMapping}
      />
    ));
  }

  get choiceColumnMappings() {
    return this.choiceColumnsWithFieldMappings.reduce((columnMappings, { column, mapping, mappingIndex }) => {
      if (mapping.action !== 'map') {
        return columnMappings;
      }
      return columnMappings.concat(
        <ChoiceColumn
          key={mappingIndex}
          api={this.props.api}
          column={column}
          mapping={mapping}
          mappingIndex={mappingIndex}
          onChangeFieldMapping={this.onChangeFieldMapping}
        />
      );
    }, []);
  }

  get tableOptions() {
    const { rules, rule: { table }, tableOptions } = this.props;
    const { tableColumnOptions } = this.state;
    const loading = !tableOptions || !tableColumnOptions;
    if (!table) {
      return null;
    }
    const { choiceColumnActions } = this;
    return (
      <Stack vertical>
        <Stack vertical>
          <hr />
          <Heading element="h3" secondary>
            Field Mappings
          </Heading>
          <Stack vertical>
            {this.fieldMappings}
          </Stack>
          <Stack>
            <br />
            <Button disabled={loading} onClick={this.onAddFieldMapping} size="slim" outline>Add a field mapping</Button>
          </Stack>
        </Stack>
        <Stack vertical>
          {choiceColumnActions.length ? (
            <>
              <hr />
              <Heading element="h3" secondary>
                Choice Column Actions
              </Heading>
              <Stack vertical>
                {choiceColumnActions}
              </Stack>
            </>
          ) : null}
        </Stack>
        {this.choiceColumnMappings}
        <Stack vertical>
          <hr />
          <Heading element="h3" secondary>
            Reference Mappings
          </Heading>
          <Stack vertical>
            {this.referenceMappings}
          </Stack>
          <Stack>
            <br />
            <Button disabled={loading || !rules.length} onClick={this.onAddReferenceMapping} size="slim" outline>Add a reference mapping</Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  get openRule() {
    const { rule: { event, action, table, addAttachment }, tableOptions, onRemove } = this.props;
    const { tableColumnOptions, error } = this.state;
    const loading = !tableOptions || !tableColumnOptions;
    return (
      <Card shadow>
        <FormLayout>
          <FormLayout.Group>
            <Select
              label="Event"
              options={Rule.eventOptions}
              value={event}
              onChange={this.onChangeEvent}
              required
            />
            {this.visitorTypes}
          </FormLayout.Group>
          <FormLayout.Group>
            <Select
              label="Action"
              options={Rule.actionOptions}
              value={action}
              onChange={this.onChangeAction}
              required
            />
            <Select
              label="ServiceNow Table"
              isLoading={!tableOptions}
              options={tableOptions}
              value={table}
              error={error}
              onChange={this.onChangeTable}
              required
            />
          </FormLayout.Group>
          <FormLayout.Group>
            {event === 'ticket_created' ? (
              <Checkbox
                label="Add ticket attachment"
                onChange={this.onChangeAddAttachment}
                checked={addAttachment || false}
              />
            ): null}
          </FormLayout.Group>

          {this.tableOptions}

          <Stack distribution="equalSpacing">
            <ButtonGroup>
              <Button onClick={this.props.onCancel} outline>
                Cancel
              </Button>
              <Button disabled={loading} onClick={this.props.onDone}>
                Done
              </Button>
            </ButtonGroup>
            {onRemove && (<Button onClick={onRemove} plain>Remove</Button>)}
          </Stack>
        </FormLayout>
      </Card>
    );
  }

  get closedRule() {
    const { rule: { event, action, table }, tableOptions } = this.props;
    const eventLabel = Rule.eventOptions.find(o => o.value === event).label;
    const actionLabel = Rule.actionOptions.find(o => o.value === action).label;
    const tableLabel = tableOptions.find(o => o.value === table).label;

    return (
      <Card>
        <p>
          <strong>{eventLabel}</strong> will <strong>{actionLabel}</strong> ServiceNow <strong>{tableLabel}</strong>.
        </p>
        <hr />
        <ButtonGroup>
          <Button onClick={this.onEdit}>
            Edit
          </Button>
        </ButtonGroup>
      </Card>
    );
  }

  render() {
    return (
      <div className="rule">
        {this.props.isOpen ? this.openRule : this.closedRule}
      </div>
    );
  }

  static get EVENT_SIGN_IN() {
    return 'entry_sign_in';
  }

  static get EVENT_TICKET_CREATED() {
    return 'ticket_created';
  }

  static get eventOptions() {
    return [
      // {
      //   label: 'Entry sign-in',
      //   value: this.EVENT_SIGN_IN,
      //   disabled: true,
      // },
      {
        label: 'Ticket created',
        value: this.EVENT_TICKET_CREATED,
      },
    ];
  }

  // TODO Allow only 'Always Create' when event is Ticket created and not a reference mapping
  static get actionOptions() {
    return [
      {
        label: 'Create or update',
        value: 'create_or_update',
      },
      {
        label: 'Always create',
        value: 'always_create',
      },
      {
        label: 'Find and update',
        value: 'find_and_update',
      },
      {
        label: 'Find or create',
        value: 'find_or_create',
      },
      {
        label: 'Find',
        value: 'find',
      }
    ];
  }

  static get envoyFieldOptions() {
    return {
      [Rule.EVENT_SIGN_IN]: [
        {
          label: 'Visitor First Name',
          value: 'visitor_first_name',
        },
        {
          label: 'Visitor Last Name',
          value: 'visitor_last_name',
        },
        {
          label: 'Visitor Full Name',
          value: 'visitor_full_name',
        },
        {
          label: 'Visitor Email Domain',
          value: 'visitor_email_domain',
        },
        {
          label: 'Visitor Type',
          value: 'visitor_type',
        },
        {
          label: 'Host First Name',
          value: 'host_first_name',
        },
        {
          label: 'Host Last Name',
          value: 'host_last_name',
        },
        {
          label: 'Host Full Name',
          value: 'host_full_name',
        },
        {
          label: 'Host Email',
          value: 'host_email',
        },
        {
          label: 'Sign-in Date & Time',
          value: 'sign_in_datetime',
        },
        {
          label: 'Sign-in Date',
          value: 'sign_in_date',
        },
        {
          label: 'Sign-out Date & Time',
          value: 'sign_out_datetime',
        },
        {
          label: 'Sign-out Date',
          value: 'sign_out_date',
        },
      ],
      [Rule.EVENT_TICKET_CREATED]: [
        {
          label: 'Request Type',
          value: 'request_type',
        },
        {
          label: 'Subject',
          value: 'subject',
        },
        {
          label: 'Description',
          value: 'description'
        },
        {
          label: 'Category',
          value: 'category',
        },
        {
          label: 'Subcategory',
          value: 'subcategory',
        },
        {
          label: 'Requestor Email',
          value: 'requestor_email',
        },
        {
          label: 'Requestor First Name',
          value: 'requestor_first_name',
        },
        {
          label: 'Requestor Last Name',
          value: 'requestor_last_name',
        },
        {
          label: 'Requestor Full Name',
          value: 'requestor_full_name',
        },
        {
          label: 'Location',
          value: 'location',
        },
        {
          label: 'Priority',
          value: 'priority'
        },
        {
          label: 'Source',
          value: 'source',
        },
        {
          label: 'Created Date & Time',
          value: 'created_at_datetime',
        },
        {
          label: 'Created Date',
          value: 'created_at_date',
        },
      ]
    };
  }

  static get defaultRule() {
    return {
      event: this.EVENT_TICKET_CREATED,
      visitorType: null,
      action: this.actionOptions[0].value,
      fieldMappings: [],
      referenceMappings: [],
      addAttachment: false,
    };
  }
}

export default Rule;